import React, { useEffect, useState, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import liveTrackService from "../Services/liveTrackService";
import { googleMapsApiKey } from "../../config/clientsConfig";
import { mapLoaderConfig } from "../Services/mapLoaderConfig";

const containerStyle = {
  width: '100%',
  height: '100vh'
};

const center = {
  lat: 12.97,
  lng: 80.15
};

const LiveMap = ({selectedTeam , propertyLocationData}) => {
  const [locations, setLocations] = useState([]);
  const [map, setMap] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [geocodedLocations, setGeocodedLocations] = useState(new Set());
  const [locationNames, setLocationNames] = useState(new Map());
  const [geocodingLoading, setGeocodingLoading] = useState(new Set());
    const [propertyLocations, setPropertyLocations] = useState( propertyLocationData || []);
   const [selectedPropertyMarker, setSelectedPropertyMarker] = useState( null);
  const hoverTimeoutRef = useRef(null);

  const { isLoaded } = useJsApiLoader(mapLoaderConfig);

  const fetchLiveLocationsMultiple = async () => {
    try {
      const response = await liveTrackService.getLiveTrackMultiple(selectedTeam.value);
      const rawLocations = response.data.locations || [];
  
     
      const formatted = rawLocations.map((loc, index) => {
        const pointId = `location-${index}-${loc.geoPoint?.[0] || 0}-${loc.geoPoint?.[1] || 0}`;
        return {
          ...loc,
          id: `location-${index}-${Date.now()}`, // Add unique ID
          geoPoint: { lat: loc.geoPoint[0], lng: loc.geoPoint[1] },
          name: "Loading...",
          person: loc.userName || 'Unknown User', 
          pointId: pointId,
          status: loc.status || (loc.isLive ? "Active" : "Inactive"),
          batteryPercentage: loc.batteryPercentage || "0"
        };
      });
  
      console.table(formatted);
      setLocations(formatted);
      setGeocodedLocations(new Set()); // Reset geocoded locations
      setLocationNames(new Map()); // Reset location names
    } catch (error) {
      console.error("Error fetching live locations:", error);
    }
  };
  
  useEffect(() => {
    
      fetchLiveLocationsMultiple();
    
  }, [selectedTeam]);

  // Enhanced reverse geocoding using Google Maps
  const getLocationName = async (lat, lng, pointId) => {
    if (geocodingLoading.has(pointId) || locationNames.has(pointId)) return "Loading...";
    
    setGeocodingLoading(prev => new Set([...prev, pointId]));
    
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const result = data.results[0];
        const address = result.formatted_address;
        
        // Extract meaningful address components
        const addressComponents = result.address_components;
        let shortAddress = "";
        let area = "";
        let city = "";
        
        // Find area/locality and city
        for (const component of addressComponents) {
          if (component.types.includes('sublocality_level_1') || 
              component.types.includes('locality')) {
            if (!area) area = component.long_name;
          }
          if (component.types.includes('administrative_area_level_2') ||
              component.types.includes('administrative_area_level_1')) {
            if (!city) city = component.long_name;
          }
        }
        
        // Create a readable short address
        shortAddress = area && city ? `${area}, ${city}` : 
                      area ? area : 
                      city ? city : 
                      address.split(',').slice(0, 2).join(',').trim();
        
        setLocationNames(prev => new Map(prev.set(pointId, {
          fullAddress: address,
          shortAddress: shortAddress,
          area: area,
          city: city
        })));
        
        return shortAddress;
      } else {
        setLocationNames(prev => new Map(prev.set(pointId, {
          fullAddress: "Unknown Location",
          shortAddress: "Unknown Location",
          area: "",
          city: ""
        })));
        return "Unknown Location";
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      setLocationNames(prev => new Map(prev.set(pointId, {
        fullAddress: "Location not available",
        shortAddress: "Location not available",
        area: "",
        city: ""
      })));
      return "Location not found";
    } finally {
      setGeocodingLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(pointId);
        return newSet;
      });
    }
  };

  // Fetch names when locations change
  useEffect(() => {
    const fetchLocationNames = async () => {
      const locationsToGeocode = locations.filter(
        loc => loc.name === "Loading..." && !geocodedLocations.has(loc.id)
      );

      if (locationsToGeocode.length === 0) return;

      const updatedLocations = [...locations];
      
      for (const location of locationsToGeocode) {
        const locationIndex = locations.findIndex(loc => loc.id === location.id);
        if (locationIndex !== -1) {
          const name = await getLocationName(
            location.geoPoint.lat,
            location.geoPoint.lng,
            location.pointId
          );
          updatedLocations[locationIndex] = { ...location, name };
          
          // Mark this location as geocoded
          setGeocodedLocations(prev => new Set([...prev, location.id]));
        }
      }
      
      setLocations(updatedLocations);
    };

    if (locations.length > 0 && locations.some(loc => loc.name === "Loading...")) {
      fetchLocationNames();
    }
  }, [locations, geocodedLocations]);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const getPropertyMarkerIcon = () => {
    return {
      url: "",
      scaledSize: new window.google.maps.Size(32, 40),
      anchor: new window.google.maps.Point(16, 40),
      
    };
  };

  // Handle marker hover with debouncing
  const handleMarkerHover = useCallback((index) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMarker(index);
    }, 100); // Small delay to prevent flickering
  }, []);

  const handleMarkerLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMarker(null);
    }, 200); // Delay before hiding
  }, []);

  // Enhanced marker icon with better styling
  const createCustomMarkerIcon = (index, person, teamName) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', 
      '#FFD93D', '#6BCF7F', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ];
    
    const bgColor = colors[index % colors.length];
    const fullName = person || 'Unknown User';
    
    // Handle name display - show full name but truncate if too long
    let displayName = fullName;
    if (fullName.length > 12) {
      // Show first part + "..." for long names like "Johnathan Rodriguez" → "Johnathan..."
      displayName = fullName.substring(0, 10) + '...';
    }
    
    // Calculate dynamic width based on name length
    const nameWidth = Math.max(displayName.length * 7, 80); // Minimum 80px
    const totalWidth = nameWidth + 35; // Add space for number circle
    
    const svgIcon = `
      <svg width="${totalWidth}" height="40" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="shadow${index}" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        
        <!-- Main marker bubble -->
        <rect x="2" y="2" width="${totalWidth - 4}" height="32" rx="16" ry="16" 
              fill="${bgColor}" stroke="white" stroke-width="2" filter="url(#shadow${index})"/>
        
        <!-- Number circle -->
        <circle cx="18" cy="18" r="12" fill="white" stroke="${bgColor}" stroke-width="2"/>
        <text x="18" y="23" text-anchor="middle" font-family="Arial, sans-serif" 
              font-size="11" font-weight="bold" fill="${bgColor}">${index + 1}</text>
        
        <!-- Full name text with better positioning -->
        <text x="35" y="23" font-family="Arial, sans-serif" font-size="11" 
              font-weight="600" fill="white" title="${fullName}">${displayName}</text>
        
        <!-- Pointer tail -->
        <polygon points="${totalWidth/2},34 ${totalWidth/2+5},38 ${totalWidth/2-5},38" 
                 fill="${bgColor}" stroke="white" stroke-width="1"/>
      </svg>
    `;
    
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgIcon),
      scaledSize: new window.google.maps.Size(totalWidth, 40),
      anchor: new window.google.maps.Point(totalWidth/2, 38),
      labelOrigin: new window.google.maps.Point(totalWidth/2, 18)
    };
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={11}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >

       {/* Property markers */}
              {propertyLocations.map((property, index) => (
                <Marker
                  key={`property-${index}`}
                  position={{ lat: property.geoPoint[0], lng: property.geoPoint[1] }}
                  icon={getPropertyMarkerIcon()}
                  zIndex={400}
                  onClick={() => setSelectedPropertyMarker(index)}
                >
                  {selectedPropertyMarker === index && (
                    <InfoWindow
                      position={{ lat: property.geoPoint[0], lng: property.geoPoint[1] }}
                      onCloseClick={() => setSelectedPropertyMarker(null)}
                    >
                      <div className="p-3 min-w-[200px] max-w-[300px]">
                        <h4 className="font-bold text-gray-800 text-lg">{property.propertyName}</h4>
                        <p className="text-sm text-gray-600 mt-1">Coordinates: {property.geoPoint[0].toFixed(6)}, {property.geoPoint[1].toFixed(6)}</p>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              ))}
     
      {locations.map((loc, index) => (
        <Marker
          key={loc.id || index}
          position={loc.geoPoint}
          icon={createCustomMarkerIcon(index, loc.person, loc.teamName)}
          onMouseOver={() => handleMarkerHover(index)}
          onMouseOut={handleMarkerLeave}
        />
      ))}

      {/* InfoWindow for hovered marker */}
      {hoveredMarker !== null && locations[hoveredMarker] && (
        <InfoWindow
          position={locations[hoveredMarker].geoPoint}
          onCloseClick={() => setHoveredMarker(null)}
          options={{
            disableAutoPan: true, // Prevents map panning when InfoWindow opens
            pixelOffset: new window.google.maps.Size(0, -30) // Offset to position above marker
          }}
        >
          <div style={{ 
            padding: "10px", 
            minWidth: "250px",
            maxWidth: "320px",
            fontFamily: "Arial, sans-serif",
            lineHeight: "1.4"
          }}>
            <div style={{ 
              fontWeight: "bold", 
              fontSize: "15px",
              marginBottom: "6px",
              color: "#2c3e50",
              borderBottom: "2px solid #3498db",
              paddingBottom: "4px"
            }}>
              👤 {locations[hoveredMarker].person || locations[hoveredMarker].userName}
            </div>
            
            <div style={{ 
              fontSize: "12px", 
              marginBottom: "4px",
              color: "#7f8c8d"
            }}>
              🏢 <strong>Team:</strong> {locations[hoveredMarker].teamName || 'No Team'}
            </div>
            
            <div style={{ 
              fontSize: "12px", 
              marginBottom: "4px",
              color: "#7f8c8d"
            }}>
              📍 <strong>Location:</strong> {(() => {
                const locationData = locationNames.get(locations[hoveredMarker].pointId);
                const isLoading = geocodingLoading.has(locations[hoveredMarker].pointId);
                
                if (isLoading) {
                  return "Loading address...";
                } else if (locationData && locationData.shortAddress !== "Unknown Location") {
                  return locationData.shortAddress;
                } else {
                  return locations[hoveredMarker].name || "Unknown Location";
                }
              })()}
            </div>
            
            <div style={{ 
              fontSize: "12px", 
              marginBottom: "4px",
              color: "#7f8c8d"
            }}>
              🔋 <strong>Battery:</strong> 
              <span style={{ 
                color: parseInt(locations[hoveredMarker].batteryPercentage) > 50 ? '#27ae60' : 
                       parseInt(locations[hoveredMarker].batteryPercentage) > 20 ? '#f39c12' : '#e74c3c',
                fontWeight: 'bold',
                marginLeft: '4px'
              }}>
                {locations[hoveredMarker].batteryPercentage}
              </span>
            </div>
            
            <div style={{ 
              fontSize: "12px", 
              marginBottom: "4px",
              color: "#7f8c8d"
            }}>
              📊 <strong>Distance:</strong> {locations[hoveredMarker].travelDistance || "0"} km
            </div>
            
            {locations[hoveredMarker].timestamp && (
              <div style={{ 
                fontSize: "12px", 
                marginBottom: "4px",
                color: "#7f8c8d"
              }}>
                ⏰ <strong>Last Update:</strong>{" "}
                {new Date(locations[hoveredMarker].timestamp).toLocaleString()}
              </div>
            )}
            
            {locations[hoveredMarker].speed !== undefined && (
              <div style={{ 
                fontSize: "12px", 
                marginBottom: "4px",
                color: "#7f8c8d"
              }}>
                🚗 <strong>Speed:</strong> {locations[hoveredMarker].speed} km/h
              </div>
            )}
            
            <div style={{ 
              fontSize: "12px", 
              marginBottom: "0",
              color: "#7f8c8d"
            }}>
              
            </div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default LiveMap;